import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle,
  Clock,
  ClipboardText,
  MagicWand,
  PawPrint,
  Play,
  Prohibit,
  Scissors,
  WarningCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import {
  useAddGroomingFieldTestFeedback,
  useAddGroomingHandoffNote,
  useGroomingSessions,
  useCommitGroomingMediaUpload,
  useCreateGroomingMediaUploadTicket,
  useStartGroomingIntake,
  useUpdateGroomingStatus,
  useUpdateGroomingWorkflow,
  useUpsertGroomingReport,
  type GroomingCoatCondition,
  type GroomingFieldTestFeedbackType,
  type GroomingOperationEvent,
  type GroomingMediaKind,
  type GroomingOperationTiming,
  type GroomingSessionStatus,
  type GroomingTemperament,
  type GroomingWorkflowStepStatus,
} from '@/features/grooming';
import { useCurrentBranch } from '@/shared/context/TenantContext';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge, type StatusVariant } from '@/shared/ui/StatusBadge';
import { formatDateTime } from '@/shared/utils/formatters';

const STATUS_LABELS: Record<GroomingSessionStatus | 'not_started', string> = {
  not_started: 'Bekliyor',
  intake: 'Intake',
  ready: 'Hazır',
  in_progress: 'İşlemde',
  paused: 'Durakladı',
  completed: 'Tamamlandı',
  cancelled: 'İptal',
};

const STATUS_VARIANTS: Record<GroomingSessionStatus | 'not_started', StatusVariant> = {
  not_started: 'pending',
  intake: 'info',
  ready: 'warning',
  in_progress: 'info',
  paused: 'warning',
  completed: 'success',
  cancelled: 'danger',
};

const COAT_OPTIONS: readonly GroomingCoatCondition[] = [
  'normal',
  'matted',
  'shedding',
  'sensitive_skin',
  'parasite_risk',
];

const TEMPERAMENT_OPTIONS: readonly GroomingTemperament[] = [
  'calm',
  'anxious',
  'reactive',
  'aggressive',
  'senior',
];

interface IntakeForm {
  coatCondition: GroomingCoatCondition;
  temperament: GroomingTemperament;
  mattingLevel: string;
  requestedStyle: string;
  specialInstructions: string;
  safetyFlags: string;
}

interface ReportForm {
  weightKg: string;
  heightCm: string;
  coatNotes: string;
  skinNotes: string;
  behaviorNotes: string;
  healthNotes: string;
  groomerNotes: string;
  customerSummary: string;
  beforePhotoUrls: string;
  afterPhotoUrls: string;
}

interface FieldTestFeedbackForm {
  type: GroomingFieldTestFeedbackType;
  score: string;
  note: string;
}

const DEFAULT_INTAKE: IntakeForm = {
  coatCondition: 'normal',
  temperament: 'calm',
  mattingLevel: '0',
  requestedStyle: '',
  specialInstructions: '',
  safetyFlags: '',
};

const DEFAULT_REPORT: ReportForm = {
  weightKg: '',
  heightCm: '',
  coatNotes: '',
  skinNotes: '',
  behaviorNotes: '',
  healthNotes: '',
  groomerNotes: '',
  customerSummary: '',
  beforePhotoUrls: '',
  afterPhotoUrls: '',
};

const DEFAULT_FIELD_TEST_FEEDBACK: FieldTestFeedbackForm = {
  type: 'workflow',
  score: '4',
  note: '',
};

export function GroomingPage(): JSX.Element {
  const branch = useCurrentBranch();
  const branchId = branch?.branchId;
  const sessions = useGroomingSessions(branchId);
  const startIntake = useStartGroomingIntake(branchId);
  const updateStatus = useUpdateGroomingStatus(branchId);
  const updateWorkflow = useUpdateGroomingWorkflow(branchId);
  const addFieldTestFeedback = useAddGroomingFieldTestFeedback(branchId);
  const addHandoffNote = useAddGroomingHandoffNote(branchId);
  const upsertReport = useUpsertGroomingReport(branchId);
  const createMediaTicket = useCreateGroomingMediaUploadTicket();
  const commitMedia = useCommitGroomingMediaUpload(branchId);

  const [selectedBookingId, setSelectedBookingId] = useState<string>('');
  const [intake, setIntake] = useState<IntakeForm>(DEFAULT_INTAKE);
  const [report, setReport] = useState<ReportForm>(DEFAULT_REPORT);
  const [beforeFile, setBeforeFile] = useState<File | null>(null);
  const [afterFile, setAfterFile] = useState<File | null>(null);
  const [handoffNote, setHandoffNote] = useState('');
  const [fieldTestFeedback, setFieldTestFeedback] = useState<FieldTestFeedbackForm>(DEFAULT_FIELD_TEST_FEEDBACK);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const items = sessions.data ?? [];
  const selected = useMemo(
    () => items.find((item) => item.bookingId === selectedBookingId) ?? items[0] ?? null,
    [items, selectedBookingId],
  );
  const activeBookingId = selected?.bookingId ?? '';

  const summary = useMemo(() => {
    const notStarted = items.filter((item) => !item.sessionId).length;
    const inProgress = items.filter((item) => item.status === 'in_progress').length;
    const completed = items.filter((item) => item.status === 'completed').length;
    const attention = items.filter((item) => item.operationTiming.attentionLevel !== 'normal').length;
    return { notStarted, inProgress, completed, attention };
  }, [items]);

  const reportPublishReady = useMemo(() => isReportPublishReady(report), [report]);

  const handleStartIntake = async () => {
    if (!activeBookingId) return;
    try {
      await startIntake.mutateAsync({
        bookingId: activeBookingId,
        input: {
          coatCondition: intake.coatCondition,
          temperament: intake.temperament,
          mattingLevel: Number(intake.mattingLevel),
          requestedStyle: intake.requestedStyle || null,
          specialInstructions: intake.specialInstructions || null,
          safetyFlags: splitCsv(intake.safetyFlags),
          checklist: {
            ears_checked: true,
            nails_checked: true,
            coat_reviewed: true,
          },
        },
      });
      toast.success('Grooming intake kaydedildi');
    } catch {
      toast.error('Grooming intake kaydedilemedi');
    }
  };

  const handleStatus = async (status: GroomingSessionStatus) => {
    if (!selected?.sessionId) return;
    try {
      await updateStatus.mutateAsync({ sessionId: selected.sessionId, status });
      toast.success(`Durum güncellendi: ${STATUS_LABELS[status]}`);
    } catch {
      toast.error('Grooming durumu güncellenemedi');
    }
  };

  const handleReport = async (publish: boolean) => {
    if (!selected?.sessionId) return;
    try {
      await upsertReport.mutateAsync({
        sessionId: selected.sessionId,
        input: {
          weightKg: parseOptionalNumber(report.weightKg),
          heightCm: parseOptionalNumber(report.heightCm),
          coatNotes: report.coatNotes || null,
          skinNotes: report.skinNotes || null,
          behaviorNotes: report.behaviorNotes || null,
          healthNotes: report.healthNotes || null,
          groomerNotes: report.groomerNotes || null,
          customerSummary: report.customerSummary || null,
          beforePhotoUrls: splitCsv(report.beforePhotoUrls),
          afterPhotoUrls: splitCsv(report.afterPhotoUrls),
          publish,
        },
      });
      toast.success(publish ? 'Rapor kartı yayınlandı' : 'Rapor kartı kaydedildi');
    } catch {
      toast.error('Rapor kartı kaydedilemedi');
    }
  };

  const handleWorkflowStep = async (stepKey: string, status: GroomingWorkflowStepStatus) => {
    if (!selected?.sessionId) return;
    try {
      await updateWorkflow.mutateAsync({ sessionId: selected.sessionId, stepKey, status });
      toast.success('Grooming checklist güncellendi');
    } catch {
      toast.error('Grooming checklist güncellenemedi');
    }
  };

  const handleMediaUpload = async (kind: GroomingMediaKind) => {
    if (!selected?.sessionId) return;
    const file = kind === 'before' ? beforeFile : afterFile;
    if (!file) {
      toast.error('Önce bir fotoğraf seçin');
      return;
    }
    try {
      const ticket = await createMediaTicket.mutateAsync({
        sessionId: selected.sessionId,
        input: {
          kind,
          fileName: file.name,
          contentType: file.type || 'image/jpeg',
          sizeBytes: file.size,
        },
      });
      if (ticket.method === 'PUT') {
        const upload = await fetch(ticket.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!upload.ok) {
          throw new Error(`Upload failed: ${upload.status}`);
        }
      }
      await commitMedia.mutateAsync({
        sessionId: selected.sessionId,
        kind,
        objectKey: ticket.objectKey,
      });
      if (kind === 'before') setBeforeFile(null);
      if (kind === 'after') setAfterFile(null);
      toast.success(ticket.storageMode === 'r2' ? 'Fotoğraf R2’ye yüklendi' : 'Mock fotoğraf kaydedildi');
    } catch {
      toast.error('Fotoğraf yükleme tamamlanamadı');
    }
  };

  const handleHandoffNote = async () => {
    if (!selected?.sessionId || !handoffNote.trim()) return;
    try {
      await addHandoffNote.mutateAsync({
        sessionId: selected.sessionId,
        note: handoffNote,
      });
      setHandoffNote('');
      toast.success('Devralma notu eklendi');
    } catch {
      toast.error('Devralma notu eklenemedi');
    }
  };

  const handleFieldTestFeedback = async () => {
    if (!selected?.sessionId || !fieldTestFeedback.note.trim()) return;
    try {
      await addFieldTestFeedback.mutateAsync({
        sessionId: selected.sessionId,
        input: {
          type: fieldTestFeedback.type,
          score: Number(fieldTestFeedback.score),
          note: fieldTestFeedback.note,
        },
      });
      setFieldTestFeedback(DEFAULT_FIELD_TEST_FEEDBACK);
      toast.success('Saha testi geri bildirimi eklendi');
    } catch {
      toast.error('Saha testi geri bildirimi eklenemedi');
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1440px] mx-auto flex flex-col gap-biz-content-lg">
      <header className="rounded-biz-card border border-biz-border bg-gradient-to-br from-biz-surface to-biz-muted/40 p-biz-card-lg flex items-start sm:items-center justify-between gap-biz-content-lg flex-wrap">
        <div className="flex items-start gap-biz-content-lg min-w-0">
          <div className="h-12 w-12 rounded-biz-btn bg-biz-primary text-biz-primary-foreground flex items-center justify-center shrink-0">
            <Scissors size={24} weight="fill" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-biz-2xl font-bold biz-heading">Grooming Studio</h1>
            <p className="text-biz-sm text-biz-muted-foreground mt-1">
              Tablet-first intake, işlem takibi, pet rapor kartı ve shadow AI/no-show notları.
            </p>
          </div>
        </div>
        <StatusBadge
          variant={summary.inProgress > 0 ? 'info' : 'neutral'}
          label={`${summary.inProgress} işlem aktif`}
        />
      </header>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-biz-content">
        <Metric label="Bekleyen intake" value={summary.notStarted} icon={<ClipboardText size={20} />} />
        <Metric label="İşlemde" value={summary.inProgress} icon={<Play size={20} />} />
        <Metric label="Tamamlanan" value={summary.completed} icon={<Scissors size={20} />} />
        <Metric label="Dikkat" value={summary.attention} icon={<Clock size={20} />} />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-biz-content-lg">
        <Card className="overflow-hidden">
          <CardHeader>
            <div>
              <h2 className="font-semibold biz-heading">Bugünün grooming kuyruğu</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                `biz_bookings` içindeki grooming hizmetleri, session state ile birleşir.
              </p>
            </div>
          </CardHeader>
          <div className="divide-y divide-biz-border">
            {sessions.isLoading ? <QueueSkeleton /> : null}
            {!sessions.isLoading && items.length === 0 ? (
              <div className="px-biz-card py-biz-card text-biz-sm text-biz-muted-foreground">
                Bugün için grooming randevusu bulunamadı.
              </div>
            ) : null}
            {items.map((item) => {
              const key = item.status ?? 'not_started';
              return (
                <button
                  key={item.bookingId}
                  type="button"
                  onClick={() => setSelectedBookingId(item.bookingId)}
                  className="w-full min-h-biz-touch text-left px-biz-card py-biz-content-lg hover:bg-biz-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
                >
                  <div className="flex items-start justify-between gap-biz-content">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">
                        {item.petName ?? 'Petsiz'} · {item.serviceName}
                      </p>
                      <p className="text-biz-sm text-biz-muted-foreground truncate">
                        {item.customerName} · {formatDateTime(item.startsAt)}
                      </p>
                      <p className="text-biz-xs text-biz-muted-foreground mt-1">
                        {timingLabel(item, nowTick)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge variant={STATUS_VARIANTS[key]} label={STATUS_LABELS[key]} />
                      {item.operationTiming.attentionLevel !== 'normal' ? (
                        <StatusBadge
                          variant={item.operationTiming.attentionLevel === 'late' ? 'danger' : 'warning'}
                          label={item.operationTiming.attentionLevel === 'late' ? 'Gecikiyor' : 'Yakın'}
                        />
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-biz-content-lg">
          <Card>
            <CardHeader>
              <div>
                <h2 className="font-semibold biz-heading">Tablet intake</h2>
                <p className="text-biz-sm text-biz-muted-foreground">
                  Tüy, mizaç, güvenlik bayrakları ve deterministic AI not taslağı.
                </p>
              </div>
            </CardHeader>
            <CardBody className="flex flex-col gap-biz-content">
              <SelectedSummary selected={selected} />
              {selected?.sessionId ? (
                <OperationPanel
                  timing={selected.operationTiming}
                  status={selected.status}
                  nowTick={nowTick}
                />
              ) : null}
              {selected?.sessionId ? (
                <FieldTestReadinessPanel
                  workflowDone={selected.workflowProgress.done}
                  workflowTotal={selected.workflowProgress.total}
                  reportReady={reportPublishReady}
                  hasBeforePhoto={splitCsv(report.beforePhotoUrls).length > 0}
                  hasAfterPhoto={splitCsv(report.afterPhotoUrls).length > 0}
                  status={selected.status}
                />
              ) : null}
              {selected?.sessionId ? (
                <FieldTestFeedbackComposer
                  value={fieldTestFeedback}
                  loading={addFieldTestFeedback.isPending}
                  onChange={setFieldTestFeedback}
                  onSubmit={handleFieldTestFeedback}
                />
              ) : null}
              {selected?.sessionId ? (
                <WorkflowPanel
                  steps={selected.workflowSteps}
                  done={selected.workflowProgress.done}
                  total={selected.workflowProgress.total}
                  loading={updateWorkflow.isPending}
                  onChange={handleWorkflowStep}
                />
              ) : null}
              {selected?.sessionId ? (
                <HandoffHistoryPanel events={selected.operationEvents} />
              ) : null}
              {selected?.sessionId ? (
                <HandoffNoteComposer
                  value={handoffNote}
                  loading={addHandoffNote.isPending}
                  onChange={setHandoffNote}
                  onSubmit={handleHandoffNote}
                />
              ) : null}
              <Field label="Tüy durumu">
                <select
                  value={intake.coatCondition}
                  onChange={(event) =>
                    setIntake((prev) => ({
                      ...prev,
                      coatCondition: event.target.value as GroomingCoatCondition,
                    }))
                  }
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                >
                  {COAT_OPTIONS.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </Field>
              <Field label="Mizaç">
                <select
                  value={intake.temperament}
                  onChange={(event) =>
                    setIntake((prev) => ({
                      ...prev,
                      temperament: event.target.value as GroomingTemperament,
                    }))
                  }
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                >
                  {TEMPERAMENT_OPTIONS.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </Field>
              <Field label="Düğüm seviyesi (0-5)">
                <input
                  value={intake.mattingLevel}
                  onChange={(event) => setIntake((prev) => ({ ...prev, mattingLevel: event.target.value }))}
                  type="number"
                  min="0"
                  max="5"
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                />
              </Field>
              <Field label="İstenen stil">
                <input
                  value={intake.requestedStyle}
                  onChange={(event) => setIntake((prev) => ({ ...prev, requestedStyle: event.target.value }))}
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                  placeholder="Örn. kısa teddy cut"
                />
              </Field>
              <Field label="Güvenlik bayrakları">
                <input
                  value={intake.safetyFlags}
                  onChange={(event) => setIntake((prev) => ({ ...prev, safetyFlags: event.target.value }))}
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                  placeholder="anxious, senior, muzzle"
                />
              </Field>
              <Field label="Özel not">
                <textarea
                  value={intake.specialInstructions}
                  onChange={(event) => setIntake((prev) => ({ ...prev, specialInstructions: event.target.value }))}
                  className="w-full min-h-[96px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
                />
              </Field>
              <div className="flex gap-biz-content flex-wrap">
                <Button
                  type="button"
                  onClick={handleStartIntake}
                  disabled={!activeBookingId}
                  loading={startIntake.isPending}
                  leftIcon={<ClipboardText size={18} />}
                >
                  Intake kaydet
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('in_progress')}
                  disabled={!selected?.sessionId}
                  loading={updateStatus.isPending}
                  leftIcon={<Play size={18} />}
                >
                  İşleme başla
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('paused')}
                  disabled={!selected?.sessionId || selected.status !== 'in_progress'}
                  loading={updateStatus.isPending}
                >
                  Duraklat
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('ready')}
                  disabled={!selected?.sessionId || selected.status === 'completed'}
                  loading={updateStatus.isPending}
                >
                  Hazır
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleStatus('completed')}
                  disabled={!selected?.sessionId}
                  loading={updateStatus.isPending}
                >
                  Tamamla
                </Button>
              </div>
              {selected?.aiNoteDraft ? (
                <div className="rounded-biz-card border border-biz-info/30 bg-biz-info-subtle px-biz-content-lg py-biz-content">
                  <p className="text-biz-sm font-semibold flex items-center gap-2">
                    <MagicWand size={16} /> Shadow AI not taslağı
                  </p>
                  <p className="text-biz-sm mt-1">{selected.aiNoteDraft}</p>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <h2 className="font-semibold biz-heading">Pet rapor kartı</h2>
                <p className="text-biz-sm text-biz-muted-foreground">
                  Boy/kilo, cilt-tüy notları ve önce/sonra fotoğraf URL snapshotı.
                </p>
              </div>
            </CardHeader>
            <CardBody className="flex flex-col gap-biz-content">
              <div className="grid grid-cols-2 gap-biz-content">
                <Field label="Kilo (kg)">
                  <input
                    value={report.weightKg}
                    onChange={(event) => setReport((prev) => ({ ...prev, weightKg: event.target.value }))}
                    type="number"
                    min="0"
                    className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                  />
                </Field>
                <Field label="Boy (cm)">
                  <input
                    value={report.heightCm}
                    onChange={(event) => setReport((prev) => ({ ...prev, heightCm: event.target.value }))}
                    type="number"
                    min="0"
                    className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                  />
                </Field>
              </div>
              <TextArea label="Tüy notu" value={report.coatNotes} onChange={(coatNotes) => setReport((prev) => ({ ...prev, coatNotes }))} />
              <TextArea label="Cilt notu" value={report.skinNotes} onChange={(skinNotes) => setReport((prev) => ({ ...prev, skinNotes }))} />
              <TextArea label="Davranış notu" value={report.behaviorNotes} onChange={(behaviorNotes) => setReport((prev) => ({ ...prev, behaviorNotes }))} />
              <TextArea label="Sağlık notu" value={report.healthNotes} onChange={(healthNotes) => setReport((prev) => ({ ...prev, healthNotes }))} />
              <TextArea label="Groomer notu" value={report.groomerNotes} onChange={(groomerNotes) => setReport((prev) => ({ ...prev, groomerNotes }))} />
              <TextArea label="Müşteri özeti" value={report.customerSummary} onChange={(customerSummary) => setReport((prev) => ({ ...prev, customerSummary }))} />
              <CustomerReportPreview
                customerName={selected?.customerName ?? ''}
                petName={selected?.petName ?? null}
                serviceName={selected?.serviceName ?? ''}
                report={report}
              />
              <PublishReadinessPanel ready={reportPublishReady} report={report} />
              <Field label="Önce foto URL'leri">
                <input
                  value={report.beforePhotoUrls}
                  onChange={(event) => setReport((prev) => ({ ...prev, beforePhotoUrls: event.target.value }))}
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                  placeholder="https://... , r2://business-grooming/... , mock://before-1"
                />
              </Field>
              <MediaUploadBox
                label="Önce fotoğraf yükle"
                file={beforeFile}
                loading={createMediaTicket.isPending || commitMedia.isPending}
                onFile={setBeforeFile}
                onUpload={() => handleMediaUpload('before')}
              />
              <Field label="Sonra foto URL'leri">
                <input
                  value={report.afterPhotoUrls}
                  onChange={(event) => setReport((prev) => ({ ...prev, afterPhotoUrls: event.target.value }))}
                  className="w-full min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
                  placeholder="https://... , r2://business-grooming/... , mock://after-1"
                />
              </Field>
              <MediaUploadBox
                label="Sonra fotoğraf yükle"
                file={afterFile}
                loading={createMediaTicket.isPending || commitMedia.isPending}
                onFile={setAfterFile}
                onUpload={() => handleMediaUpload('after')}
              />
              <div className="rounded-biz-card border border-biz-info/30 bg-biz-info-subtle px-biz-content-lg py-biz-content text-biz-sm">
                Foto kontratı: backend presigned upload ticket üretir. R2 aktifse tarayıcı doğrudan
                R2 PUT yapar; R2 yoksa mock mode’da aynı commit kontratı çalışır.
              </div>
              <div className="flex gap-biz-content flex-wrap">
                <Button
                  type="button"
                  onClick={() => handleReport(false)}
                  disabled={!selected?.sessionId}
                  loading={upsertReport.isPending}
                  leftIcon={<PawPrint size={18} />}
                >
                  Rapor kaydet
                </Button>
                <Button
                  type="button"
                  intent="secondary"
                  onClick={() => handleReport(true)}
                  disabled={!selected?.sessionId || !reportPublishReady}
                  loading={upsertReport.isPending}
                >
                  Yayınla
                </Button>
              </div>
              {!selected?.sessionId ? (
                <div className="rounded-biz-card border border-biz-warning/30 bg-biz-warning-subtle px-biz-content-lg py-biz-content text-biz-sm flex gap-2">
                  <WarningCircle size={18} className="shrink-0" />
                  Önce intake kaydı oluşturulmalı.
                </div>
              ) : null}
            </CardBody>
          </Card>
        </div>
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: JSX.Element;
}): JSX.Element {
  return (
    <Card>
      <CardBody className="flex items-center justify-between gap-biz-content">
        <div>
          <p className="text-biz-xs text-biz-muted-foreground">{label}</p>
          <p className="text-biz-2xl font-bold biz-heading">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-biz-btn bg-biz-muted text-biz-muted-foreground flex items-center justify-center">
          {icon}
        </div>
      </CardBody>
    </Card>
  );
}

function SelectedSummary({
  selected,
}: {
  selected: {
    petName: string | null;
    customerName: string;
    serviceName: string;
    startsAt: string;
  } | null;
}): JSX.Element {
  if (!selected) {
    return (
      <div className="rounded-biz-card border border-biz-border bg-biz-muted/30 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
        Soldan bir grooming randevusu seçin.
      </div>
    );
  }
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/30 px-biz-content-lg py-biz-content">
      <p className="font-semibold">{selected.petName ?? 'Petsiz'} · {selected.serviceName}</p>
      <p className="text-biz-sm text-biz-muted-foreground">
        {selected.customerName} · {formatDateTime(selected.startsAt)}
      </p>
    </div>
  );
}

function OperationPanel({
  timing,
  status,
  nowTick,
}: {
  timing: GroomingOperationTiming;
  status: GroomingSessionStatus | null;
  nowTick: number;
}): JSX.Element {
  const liveElapsed = liveElapsedMinutes(timing, nowTick);
  const progress = Math.min(100, Math.round((liveElapsed / Math.max(1, timing.scheduledMinutes)) * 100));
  const label = timing.attentionLevel === 'late'
    ? 'Süre aşıldı'
    : timing.attentionLevel === 'watch'
      ? 'Yakından izle'
      : 'Plan içinde';
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-biz-content-lg">
      <div className="flex items-start justify-between gap-biz-content">
        <div>
          <p className="font-semibold biz-heading flex items-center gap-2">
            <Clock size={16} /> Operasyon süresi
          </p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            Planlanan {formatMinutes(timing.scheduledMinutes)} · durum {status ?? 'bekliyor'}
          </p>
        </div>
        <StatusBadge
          variant={timing.attentionLevel === 'late' ? 'danger' : timing.attentionLevel === 'watch' ? 'warning' : 'success'}
          label={label}
        />
      </div>
      <div className="mt-biz-content grid grid-cols-3 gap-biz-content text-biz-sm">
        <TimingCell label="Başlangıç" value={timing.startedAt ? formatDateTime(timing.startedAt) : 'Başlamadı'} />
        <TimingCell label="Geçen" value={timing.startedAt ? formatMinutes(liveElapsed) : timingLabelFromStart(timing)} />
        <TimingCell label="Kalan" value={formatSignedMinutes(timing.minutesUntilEnd)} />
      </div>
      <div className="mt-biz-content h-2 rounded-full bg-biz-muted overflow-hidden">
        <div
          className={`h-full rounded-full ${
            timing.attentionLevel === 'late'
              ? 'bg-biz-danger'
              : timing.attentionLevel === 'watch'
                ? 'bg-biz-warning'
                : 'bg-biz-success'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function HandoffHistoryPanel({ events }: { events: readonly GroomingOperationEvent[] }): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-background p-biz-content-lg">
      <div className="flex items-center justify-between gap-biz-content mb-biz-content">
        <div>
          <p className="font-semibold biz-heading">Devralma geçmişi</p>
          <p className="text-biz-xs text-biz-muted-foreground">
            Intake, checklist ve rapor olaylarından türetilmiş operasyon özeti.
          </p>
        </div>
        <StatusBadge variant={events.length > 0 ? 'info' : 'neutral'} label={`${events.length} kayıt`} />
      </div>
      {events.length === 0 ? (
        <div className="rounded-biz-card border border-dashed border-biz-border px-biz-content py-biz-content text-biz-sm text-biz-muted-foreground">
          Henüz devralma notu oluşmadı.
        </div>
      ) : (
        <div className="space-y-biz-content">
          {events.map((event) => (
            <div key={event.key} className="flex gap-biz-content">
              <div className={`mt-1 h-3 w-3 rounded-full shrink-0 ${eventDotClass(event.tone)}`} />
              <div className="min-w-0">
                <p className="text-biz-sm font-semibold">{event.label}</p>
                <p className="text-biz-xs text-biz-muted-foreground">
                  {formatDateTime(event.occurredAt)}
                  {event.actorId ? ` · ${event.actorId.slice(0, 8)}` : ''}
                </p>
                <p className="text-biz-sm mt-1">{event.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldTestReadinessPanel({
  workflowDone,
  workflowTotal,
  reportReady,
  hasBeforePhoto,
  hasAfterPhoto,
  status,
}: {
  workflowDone: number;
  workflowTotal: number;
  reportReady: boolean;
  hasBeforePhoto: boolean;
  hasAfterPhoto: boolean;
  status: GroomingSessionStatus | null;
}): JSX.Element {
  const checks = [
    { label: 'İşlem başlatıldı', ok: status === 'in_progress' || status === 'paused' || status === 'completed' },
    { label: 'Checklist tamamlandı', ok: workflowTotal > 0 && workflowDone === workflowTotal },
    { label: 'Rapor yayına hazır', ok: reportReady },
    { label: 'Önce/sonra foto seti', ok: hasBeforePhoto && hasAfterPhoto },
  ];
  const passed = checks.filter((check) => check.ok).length;
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface p-biz-content-lg">
      <div className="flex items-center justify-between gap-biz-content">
        <div>
          <p className="font-semibold biz-heading">Saha testi hazırlığı</p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            Partner field-test öncesi operasyon yüzeyinin tamamlanma kontrolü.
          </p>
        </div>
        <StatusBadge
          variant={passed === checks.length ? 'success' : passed >= 2 ? 'warning' : 'pending'}
          label={`${passed}/${checks.length}`}
        />
      </div>
      <div className="mt-biz-content grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
        {checks.map((check) => (
          <div key={check.label} className="flex items-center gap-2 text-biz-sm">
            <CheckCircle
              size={16}
              weight={check.ok ? 'fill' : 'regular'}
              className={check.ok ? 'text-biz-success' : 'text-biz-muted-foreground'}
            />
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldTestFeedbackComposer({
  value,
  loading,
  onChange,
  onSubmit,
}: {
  value: FieldTestFeedbackForm;
  loading: boolean;
  onChange: (value: FieldTestFeedbackForm) => void;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content-lg">
      <p className="font-semibold biz-heading">Saha testi geri bildirimi</p>
      <p className="text-biz-xs text-biz-muted-foreground mt-1">
        Partner/groomer field-test notlarını 1-5 skorla append-only olarak kaydeder.
      </p>
      <div className="mt-biz-content grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-biz-content">
        <select
          value={value.type}
          onChange={(event) => onChange({ ...value, type: event.target.value as GroomingFieldTestFeedbackType })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
        >
          <option value="workflow">Workflow</option>
          <option value="usability">Kullanılabilirlik</option>
          <option value="report">Rapor</option>
          <option value="media">Medya</option>
          <option value="other">Diğer</option>
        </select>
        <select
          value={value.score}
          onChange={(event) => onChange({ ...value, score: event.target.value })}
          className="min-h-biz-touch rounded-biz-input border border-biz-input bg-biz-background px-biz-content"
        >
          {[1, 2, 3, 4, 5].map((score) => (
            <option key={score} value={String(score)}>{score}/5</option>
          ))}
        </select>
      </div>
      <textarea
        value={value.note}
        onChange={(event) => onChange({ ...value, note: event.target.value })}
        className="mt-biz-content w-full min-h-[84px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
        placeholder="Örn. Foto yükleme akışı anlaşılır, ancak checklist butonları daha büyük olabilir."
      />
      <div className="mt-biz-content flex justify-end">
        <Button
          type="button"
          intent="secondary"
          disabled={value.note.trim().length < 3}
          loading={loading}
          onClick={onSubmit}
        >
          Feedback ekle
        </Button>
      </div>
    </div>
  );
}

function HandoffNoteComposer({
  value,
  loading,
  onChange,
  onSubmit,
}: {
  value: string;
  loading: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 p-biz-content-lg">
      <p className="font-semibold biz-heading">Devralma notu ekle</p>
      <p className="text-biz-xs text-biz-muted-foreground mt-1">
        Vardiya değişimi, özel dikkat veya müşteri beklentisi gibi bilgileri append-only not olarak ekler.
      </p>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-biz-content w-full min-h-[84px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
        placeholder="Örn. Sol arka patide hassasiyet var, kurutmada düşük ısı kullan."
      />
      <div className="mt-biz-content flex justify-end">
        <Button
          type="button"
          intent="secondary"
          disabled={value.trim().length < 3}
          loading={loading}
          onClick={onSubmit}
        >
          Notu ekle
        </Button>
      </div>
    </div>
  );
}

function CustomerReportPreview({
  customerName,
  petName,
  serviceName,
  report,
}: {
  customerName: string;
  petName: string | null;
  serviceName: string;
  report: ReportForm;
}): JSX.Element {
  const beforeCount = splitCsv(report.beforePhotoUrls).length;
  const afterCount = splitCsv(report.afterPhotoUrls).length;
  const summary = report.customerSummary
    || report.groomerNotes
    || 'Rapor özeti yayınlamadan önce burada görünecek.';
  return (
    <div className="rounded-biz-card border border-biz-info/30 bg-biz-info-subtle p-biz-content-lg">
      <div className="flex items-start justify-between gap-biz-content">
        <div>
          <p className="text-biz-sm font-semibold biz-heading">Müşteri önizlemesi</p>
          <p className="text-biz-xs text-biz-muted-foreground mt-1">
            {customerName || 'Müşteri'} · {petName ?? 'Pet'} · {serviceName || 'Grooming'}
          </p>
        </div>
        <StatusBadge variant="info" label={`${beforeCount + afterCount} foto`} />
      </div>
      <p className="mt-biz-content text-biz-sm">{summary}</p>
      <div className="mt-biz-content grid grid-cols-2 gap-biz-content text-biz-xs">
        <PreviewChip label="Tüy" value={report.coatNotes || 'Not yok'} />
        <PreviewChip label="Cilt" value={report.skinNotes || 'Not yok'} />
        <PreviewChip label="Davranış" value={report.behaviorNotes || 'Not yok'} />
        <PreviewChip label="Sağlık" value={report.healthNotes || 'Not yok'} />
      </div>
    </div>
  );
}

function PublishReadinessPanel({ ready, report }: { ready: boolean; report: ReportForm }): JSX.Element {
  const checks = [
    { label: 'Müşteri özeti', ok: report.customerSummary.trim().length > 0 },
    { label: 'En az bir bakım/sağlık notu', ok: hasCareNote(report) },
  ];
  return (
    <div className={`rounded-biz-card border px-biz-content-lg py-biz-content ${
      ready
        ? 'border-biz-success/30 bg-biz-success-subtle'
        : 'border-biz-warning/30 bg-biz-warning-subtle'
    }`}>
      <div className="flex items-center justify-between gap-biz-content">
        <p className="text-biz-sm font-semibold">Yayın kalite kapısı</p>
        <StatusBadge variant={ready ? 'success' : 'warning'} label={ready ? 'Yayına hazır' : 'Eksik'} />
      </div>
      <div className="mt-biz-content grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
        {checks.map((check) => (
          <div key={check.label} className="flex items-center gap-2 text-biz-sm">
            <CheckCircle
              size={16}
              weight={check.ok ? 'fill' : 'regular'}
              className={check.ok ? 'text-biz-success' : 'text-biz-muted-foreground'}
            />
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewChip({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-biz-card bg-biz-background border border-biz-border px-biz-content py-biz-content">
      <p className="text-biz-muted-foreground">{label}</p>
      <p className="font-medium truncate mt-1">{value}</p>
    </div>
  );
}

function eventDotClass(tone: GroomingOperationEvent['tone']): string {
  switch (tone) {
    case 'success':
      return 'bg-biz-success';
    case 'warning':
      return 'bg-biz-warning';
    case 'danger':
      return 'bg-biz-danger';
    case 'info':
      return 'bg-biz-info';
    default:
      return 'bg-biz-muted-foreground';
  }
}

function TimingCell({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-biz-card bg-biz-background border border-biz-border px-biz-content py-biz-content">
      <p className="text-biz-xs text-biz-muted-foreground">{label}</p>
      <p className="font-semibold mt-1">{value}</p>
    </div>
  );
}

function WorkflowPanel({
  steps,
  done,
  total,
  loading,
  onChange,
}: {
  steps: readonly {
    key: string;
    label: string;
    status: GroomingWorkflowStepStatus;
  }[];
  done: number;
  total: number;
  loading: boolean;
  onChange: (stepKey: string, status: GroomingWorkflowStepStatus) => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-biz-content-lg">
      <div className="flex items-center justify-between gap-biz-content mb-biz-content">
        <div>
          <p className="font-semibold biz-heading">İşlem checklist</p>
          <p className="text-biz-xs text-biz-muted-foreground">
            {done}/{total} adım tamamlandı
          </p>
        </div>
        <StatusBadge
          variant={done === total ? 'success' : 'pending'}
          label={done === total ? 'Hazır' : 'Devam'}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
        {steps.map((step) => (
          <div
            key={step.key}
            className="rounded-biz-card border border-biz-border bg-biz-background px-biz-content py-biz-content"
          >
            <div className="flex items-start justify-between gap-biz-content">
              <div className="min-w-0">
                <p className="text-biz-sm font-medium truncate">{step.label}</p>
                <p className="text-biz-xs text-biz-muted-foreground">{step.status}</p>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => onChange(step.key, step.status === 'done' ? 'pending' : 'done')}
                  className="min-h-biz-touch-sm min-w-biz-touch-sm rounded-biz-btn border border-biz-border inline-flex items-center justify-center disabled:opacity-60"
                  aria-label={`${step.label} tamamlandı`}
                >
                  <CheckCircle
                    size={18}
                    className={step.status === 'done' ? 'text-biz-success' : 'text-biz-muted-foreground'}
                    weight={step.status === 'done' ? 'fill' : 'regular'}
                  />
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => onChange(step.key, step.status === 'blocked' ? 'pending' : 'blocked')}
                  className="min-h-biz-touch-sm min-w-biz-touch-sm rounded-biz-btn border border-biz-border inline-flex items-center justify-center disabled:opacity-60"
                  aria-label={`${step.label} bloklandı`}
                >
                  <Prohibit
                    size={18}
                    className={step.status === 'blocked' ? 'text-biz-danger' : 'text-biz-muted-foreground'}
                    weight={step.status === 'blocked' ? 'fill' : 'regular'}
                  />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-biz-sm font-medium">
      <span>{label}</span>
      {children}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <Field label={label}>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full min-h-[84px] rounded-biz-input border border-biz-input bg-biz-background p-biz-content"
      />
    </Field>
  );
}

function MediaUploadBox({
  label,
  file,
  loading,
  onFile,
  onUpload,
}: {
  label: string;
  file: File | null;
  loading: boolean;
  onFile: (file: File | null) => void;
  onUpload: () => void;
}): JSX.Element {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-muted/20 px-biz-content-lg py-biz-content flex flex-col gap-biz-content">
      <div>
        <p className="text-biz-sm font-semibold">{label}</p>
        <p className="text-biz-xs text-biz-muted-foreground">
          JPEG, PNG veya WEBP · maksimum 15MB · presigned PUT kontratı
        </p>
      </div>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => onFile(event.target.files?.[0] ?? null)}
        className="block w-full text-biz-sm file:min-h-biz-touch file:rounded-biz-btn file:border-0 file:bg-biz-surface file:px-biz-content-lg file:text-biz-sm file:font-medium"
      />
      {file ? (
        <p className="text-biz-xs text-biz-muted-foreground">
          Seçili: {file.name} · {Math.round(file.size / 1024)} KB
        </p>
      ) : null}
      <Button
        type="button"
        intent="secondary"
        disabled={!file}
        loading={loading}
        onClick={onUpload}
        fullWidth
      >
        Presign + yükle + rapora bağla
      </Button>
    </div>
  );
}

function QueueSkeleton(): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((idx) => (
        <div key={idx} className="px-biz-card py-biz-content-lg animate-pulse">
          <div className="h-4 w-2/3 rounded bg-biz-muted" />
          <div className="mt-2 h-3 w-1/2 rounded bg-biz-muted" />
        </div>
      ))}
    </>
  );
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseOptionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasCareNote(report: ReportForm): boolean {
  return [
    report.coatNotes,
    report.skinNotes,
    report.behaviorNotes,
    report.healthNotes,
  ].some((value) => value.trim().length > 0);
}

function isReportPublishReady(report: ReportForm): boolean {
  return report.customerSummary.trim().length > 0 && hasCareNote(report);
}

function liveElapsedMinutes(timing: GroomingOperationTiming, nowTick: number): number {
  if (!timing.startedAt) return 0;
  const end = timing.completedAt ? new Date(timing.completedAt).getTime() : nowTick;
  return Math.max(0, Math.round((end - new Date(timing.startedAt).getTime()) / 60_000));
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours} sa ${rest} dk` : `${hours} sa`;
}

function formatSignedMinutes(minutes: number): string {
  if (minutes >= 0) return formatMinutes(minutes);
  return `${formatMinutes(Math.abs(minutes))} geçti`;
}

function timingLabelFromStart(timing: GroomingOperationTiming): string {
  if (timing.minutesUntilStart > 0) return `${formatMinutes(timing.minutesUntilStart)} sonra`;
  if (timing.minutesUntilStart === 0) return 'Şimdi';
  return `${formatMinutes(Math.abs(timing.minutesUntilStart))} önce`;
}

function timingLabel(
  item: {
    status: GroomingSessionStatus | null;
    operationTiming: GroomingOperationTiming;
  },
  nowTick: number,
): string {
  const timing = item.operationTiming;
  if (item.status === 'completed') {
    return `Tamamlandı · ${formatMinutes(liveElapsedMinutes(timing, nowTick))}`;
  }
  if (item.status === 'in_progress' || item.status === 'paused') {
    return `${item.status === 'paused' ? 'Durakladı' : 'İşlemde'} · ${formatMinutes(liveElapsedMinutes(timing, nowTick))} / ${formatMinutes(timing.scheduledMinutes)}`;
  }
  return `Başlangıç ${timingLabelFromStart(timing)} · plan ${formatMinutes(timing.scheduledMinutes)}`;
}
